/**
 * Today's appointments, grouped by the hour they start (117 / 115-A2).
 *
 * ⭐ The reference's one genuinely better idea, and the reason is subtractive: a flat list
 * of five visits reads as a full day. Grouped by hour, the same five show three of them
 * before ten and nothing after two — **an empty afternoon becomes visible**, and an empty
 * afternoon is a thing an owner can sell.
 *
 * ⚠️ THE SPAN IS THE WORK, NOT A WORKING DAY. The hours run from the first booking to the
 * last and fill every gap between; they do NOT run 8–6. ServWell has no working-hours or
 * availability model anywhere — the 115 prototype struck utilization % for exactly this
 * reason, having found no denominator — so a 7am–7pm grid would be inventing the shop's
 * opening hours and then drawing conclusions from them. What this can honestly say is
 * "nothing between 11 and 2 on a day that runs to 4", and that is what it says.
 */
export type HourGroup<T> = {
  /** 0–23, in the SHOP's zone. */
  hour: number;
  label: string;
  items: T[];
};

/** `14` → `"2 PM"`. Midnight and noon are named, never `0 AM` / `12 PM`-for-midnight. */
export function hourLabel(hour: number): string {
  if (hour === 0) return 'Midnight';
  if (hour === 12) return 'Noon';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h} ${hour < 12 ? 'AM' : 'PM'}`;
}

/**
 * Group rows into contiguous hour buckets, INCLUDING the empty ones in between.
 *
 * `hourOf` is passed in rather than a date + timezone: the caller already resolved the
 * shop's clock once for the whole page, and re-deriving it per row is how one list ends up
 * bucketed in two zones.
 */
export function groupByHour<T>(
  rows: readonly T[],
  hourOf: (row: T) => number,
): HourGroup<T>[] {
  if (rows.length === 0) return [];
  const byHour = new Map<number, T[]>();
  for (const row of rows) {
    const h = hourOf(row);
    const bucket = byHour.get(h);
    if (bucket) bucket.push(row);
    else byHour.set(h, [row]);
  }
  const hours = [...byHour.keys()];
  const first = Math.min(...hours);
  const last = Math.max(...hours);
  const out: HourGroup<T>[] = [];
  for (let hour = first; hour <= last; hour++) {
    out.push({ hour, label: hourLabel(hour), items: byHour.get(hour) ?? [] });
  }
  return out;
}
